import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { useToast } from '../../context/ToastContext';
import { useConfirm } from '../../context/ConfirmContext';
import DataTable from '../common/DataTable';
import Modal from '../common/Modal';
import InvoiceForm from '../common/InvoiceForm';
import PrintPreview from '../common/PrintPreview';
import { Plus, Printer, ArrowRight, Edit2, Trash2 } from 'lucide-react';
import { formatDate } from '../../utils/helpers';
import { isChallanLinked } from '../../utils/validation';

export default function DeliveryChallan() {
  const { state, dispatch } = useApp();
  const { addToast } = useToast();
  const confirm = useConfirm();
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [printData, setPrintData] = useState(null);

  const handleSave = (data) => {
    const payload = { ...data, summary: { subtotal: data.subtotal, taxable: data.taxable, cgst: data.cgst, sgst: data.sgst, igst: data.igst, grandTotal: data.grandTotal, totalDiscount: data.totalDiscount, roundOff: data.roundOff } };
    if (editData) {
      dispatch({ type: 'DELETE_DELIVERY_CHALLAN', payload: editData.id });
      dispatch({ type: 'ADD_DELIVERY_CHALLAN', payload });
      addToast('Delivery challan updated successfully', 'success');
    } else {
      dispatch({ type: 'ADD_DELIVERY_CHALLAN', payload });
      addToast('Delivery challan created successfully', 'success');
    }
    closeForm();
  };

  const handleConvert = async (id) => {
    const ok = await confirm('Convert this challan to a Sales Invoice?', { title: 'Convert Challan', confirmLabel: 'Convert' });
    if (ok) {
      dispatch({ type: 'CONVERT_CHALLAN_TO_INVOICE', payload: id });
      addToast('Challan converted to invoice successfully', 'success');
    }
  };

  const handleEdit = (row) => {
    const links = isChallanLinked(row, state);
    if (links.length > 0) {
      addToast(`Cannot edit ${row.challanNo}: it is linked to ${links.join(', ')}. Delete the linked invoice first.`, 'error');
      return;
    }
    setEditData(row);
    setShowForm(true);
  };
  const handleDelete = (row) => {
    const links = isChallanLinked(row, state);
    if (links.length > 0) {
      addToast(`Cannot delete ${row.challanNo}: it is linked to ${links.join(', ')}. Delete the linked invoice first.`, 'error');
      return;
    }
    if (window.confirm(`Delete challan ${row.challanNo}? Stock will be reversed.`)) {
      dispatch({ type: 'DELETE_DELIVERY_CHALLAN', payload: row.id });
      addToast('Delivery challan deleted', 'success');
    }
  };
  const closeForm = () => { setShowForm(false); setEditData(null); };

  const columns = [
    { key: 'challanNo', label: 'Challan No', sortable: true },
    { key: 'date', label: 'Date', render: (v) => formatDate(v) },
    { key: 'partyId', label: 'Customer', render: (v) => state.parties.find(p => p.id === v)?.name || '-' },
    { key: 'vehicleNo', label: 'Vehicle' },
    { key: 'status', label: 'Status', render: (v) => (
      <span className={`badge ${v === 'Converted' ? 'badge-success' : 'badge-warning'}`}>{v}</span>
    )},
    { key: 'convertedTo', label: 'Invoice No' },
  ];

  if (printData) {
    return <PrintPreview type="challan" data={printData} onClose={() => setPrintData(null)} />;
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Delivery Challans</h1>
        <button onClick={() => { setEditData(null); setShowForm(true); }} className="btn btn-primary flex items-center gap-2">
          <Plus size={16} /> New Challan
        </button>
      </div>
      <div className="card">
        <DataTable
          columns={columns}
          data={[...state.deliveryChallans].reverse()}
          searchFields={['challanNo', 'vehicleNo']}
          emptyState={{ title: 'No delivery challans yet', description: 'Create your first delivery challan.', actionLabel: 'New Challan', onAction: () => setShowForm(true) }}
          actions={(row) => (
            <>
              <button onClick={() => handleEdit(row)} className="p-1.5 rounded hover:bg-amber-50 text-amber-600" title="Edit"><Edit2 size={15} /></button>
              <button onClick={() => setPrintData(row)} className="p-1.5 rounded hover:bg-green-50 text-green-600" title="Print"><Printer size={15} /></button>
              {row.status === 'Pending' && (
                <button onClick={() => handleConvert(row.id)} className="p-1.5 rounded hover:bg-blue-50 text-blue-600" title="Convert to Invoice"><ArrowRight size={15} /></button>
              )}
              <button onClick={() => handleDelete(row)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Delete"><Trash2 size={15} /></button>
            </>
          )}
        />
      </div>
      <Modal isOpen={showForm} onClose={closeForm} title={editData ? `Edit Challan ${editData.challanNo}` : 'New Delivery Challan'} size="xl">
        <InvoiceForm
          key={editData ? editData.id : 'new'}
          type="challan"
          onSave={handleSave}
          onCancel={closeForm}
          initialData={editData || undefined}
          nextEntryNo={editData ? editData.challanNo : `DC-${String(state.counters.deliveryChallan + 1).padStart(4, '0')}`}
        />
      </Modal>
    </div>
  );
}
